/**
 * SDR Stock Manager - Supabase Client, PostgREST API & Row Mappers
 * @module services/supabase
 */

import { createClient as supabaseJsCreateClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG, TABLES } from '../config/constants.js';
import { getState } from '../state/store.js';
import {
  getAuthAccessToken,
  getAuthenticatedOperatorEmail,
  refreshAuthSession
} from './auth.js';
import { normalizeBoxContents } from '../utils/box-contents.js';

let supabaseClient = null;

/**
 * Normalizes a Supabase URL to its clean origin without trailing slashes or `/rest/v1`.
 * @param {string} url
 * @returns {string}
 */
export function normalizeSupabaseUrl(url) {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl);
    return parsed.origin.replace(/\/+$/, '');
  } catch {
    return rawUrl
      .replace(/\/rest\/v1\/?.*$/i, '')
      .replace(/\/+$/, '');
  }
}

/**
 * Resolves current Supabase connection configuration.
 * Precedence: Store settings -> window.SDR_PUBLIC_CONFIG -> SUPABASE_CONFIG defaults.
 * @returns {{ url: string, anonKey: string, bucket: string }}
 */
export function getSupabaseConfig() {
  const state = getState();
  const publicCfg =
    typeof window !== 'undefined' && window.SDR_PUBLIC_CONFIG && typeof window.SDR_PUBLIC_CONFIG === 'object'
      ? window.SDR_PUBLIC_CONFIG
      : {};

  const rawUrl =
    state.settings?.supabaseUrl ||
    state.settings?.sheetUrl ||
    publicCfg.supabaseUrl ||
    publicCfg.url ||
    SUPABASE_CONFIG.DEFAULT_URL;

  const rawKey =
    state.settings?.supabaseAnonKey ||
    publicCfg.supabaseAnonKey ||
    publicCfg.anonKey ||
    SUPABASE_CONFIG.DEFAULT_ANON_KEY;

  const rawBucket =
    state.settings?.r2UploadUrl ||
    publicCfg.storageBucket ||
    publicCfg.bucket ||
    SUPABASE_CONFIG.STORAGE_BUCKET;

  return {
    url: normalizeSupabaseUrl(rawUrl),
    anonKey: String(rawKey || '').trim(),
    bucket: String(rawBucket || 'item-images').trim().toLowerCase()
  };
}

/**
 * Checks if cloud sync configuration has valid non-empty credentials.
 * @returns {boolean}
 */
export function hasCloudSyncConfig() {
  const { url, anonKey } = getSupabaseConfig();
  return Boolean(url && anonKey);
}

/**
 * Gets or initializes the Supabase client singleton instance.
 * @returns {any}
 */
export function getSupabaseClient() {
  if (supabaseClient) return supabaseClient;
  const { url, anonKey } = getSupabaseConfig();
  if (!url || !anonKey) return null;

  const createFn =
    (typeof window !== 'undefined' && window.supabase && typeof window.supabase.createClient === 'function' ? window.supabase.createClient : null) ||
    (typeof global !== 'undefined' && global.supabase && typeof global.supabase.createClient === 'function' ? global.supabase.createClient : null) ||
    supabaseJsCreateClient;

  if (typeof createFn === 'function') {
    supabaseClient = createFn(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined
      }
    });
    if (typeof window !== 'undefined') {
      window.__SDR_SUPABASE_CLIENT = supabaseClient;
    }
    return supabaseClient;
  }

  return null;
}

/**
 * Sets or overrides the active Supabase client singleton (useful for testing).
 * @param {any} client
 */
export function setSupabaseClient(client) {
  supabaseClient = client;
  if (typeof window !== 'undefined') {
    window.__SDR_SUPABASE_CLIENT = client;
  }
}

/**
 * Generates dynamic HTTP authorization headers injecting the current JWT Bearer token.
 * @param {Record<string, string>} [extra={}]
 * @returns {Record<string, string>}
 */
export function supabaseHeaders(extra = {}) {
  const { anonKey } = getSupabaseConfig();
  const token = getAuthAccessToken(anonKey);
  return {
    apikey: anonKey,
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json',
    Prefer: 'return=representation',
    ...extra
  };
}

/**
 * Executes a PostgREST HTTP request against Supabase REST API with auto-refresh on 401/403.
 * @param {string} path - Relative REST endpoint path (e.g. 'inventory?select=*')
 * @param {RequestInit} [options={}]
 * @returns {Promise<any>}
 */
export async function supabaseRequest(path, options = {}) {
  const { url } = getSupabaseConfig();
  if (!url) throw new Error('Missing Supabase URL');

  let headers = supabaseHeaders(options.headers || {});
  let response = await fetch(`${url}/rest/v1/${path}`, {
    ...options,
    headers
  });

  // Attempt automatic session refresh on 401 Unauthorized or 403 Forbidden
  if (response.status === 401 || response.status === 403) {
    const client = getSupabaseClient();
    if (client) {
      const refreshed = await refreshAuthSession(client);
      if (refreshed && refreshed.access_token) {
        headers = supabaseHeaders(options.headers || {});
        response = await fetch(`${url}/rest/v1/${path}`, {
          ...options,
          headers
        });
      }
    }
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `Supabase request failed (${response.status})`);
  }

  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Invokes a PostgreSQL Remote Procedure Call (RPC) via PostgREST.
 * @param {string} functionName
 * @param {any} [payload={}]
 * @returns {Promise<any>}
 */
function isMockClient(client) {
  return Boolean(client && (client.isMock || client.constructor?.name === 'MockSupabaseClient'));
}

export async function supabaseRpc(functionName, payload = {}) {
  const client = getSupabaseClient();
  if (isMockClient(client) && typeof client.rpc === 'function') {
    const res = await client.rpc(functionName, payload);
    if (res && res.error) {
      throw new Error(res.error.message || 'RPC Error');
    }
    return res?.data ?? res;
  }

  return supabaseRequest(`rpc/${functionName}`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

/**
 * Fetches all rows from a Supabase table with pagination.
 * @param {string} table
 * @param {Object} [options={}]
 * @param {number} [options.pageSize=300]
 * @param {string} [options.orderBy='id']
 * @param {string[]} [options.filters=[]]
 * @returns {Promise<any[]>}
 */
export async function fetchSupabaseTableRows(table, options = {}) {
  const client = getSupabaseClient();
  const defaultOrderBy = table === TABLES.APP_SETTINGS ? 'key' : (table === TABLES.USER_WHITELIST ? 'email_or_domain' : 'id');
  const orderBy = options.orderBy || defaultOrderBy;

  if (isMockClient(client) && typeof client.from === 'function') {
    let builder = client.from(table).select('*');
    if (orderBy) {
      builder = builder.order(orderBy, { ascending: true });
    }
    if (options.filters && options.filters.length > 0) {
      for (const f of options.filters) {
        const match = f.match(/^([a-zA-Z0-9_]+)=gt\.(.+)$/);
        if (match) {
          builder = builder.gt(match[1], decodeURIComponent(match[2]));
        }
      }
    }
    const { data, error } = await builder;
    if (error) {
      console.warn(`Supabase fetch ${table} note:`, error);
      throw new Error(error.message || 'Query error');
    }
    return Array.isArray(data) ? data : [];
  }

  const pageSize = options.pageSize || 300;
  const filters = options.filters || [];
  const rows = [];
  const filterQuery = filters.length ? `&${filters.join('&')}` : '';

  for (let offset = 0; ; offset += pageSize) {
    const batch = await supabaseRequest(
      `${table}?select=*${filterQuery}&order=${orderBy}.asc&limit=${pageSize}&offset=${offset}`
    );
    const pageRows = Array.isArray(batch) ? batch : [];
    rows.push(...pageRows);

    if (pageRows.length < pageSize) break;
  }

  return rows;
}

/**
 * Performs chunked upsert into a Supabase table with merge-duplicate resolution.
 * @param {string} table
 * @param {any[]} rows
 * @param {string} [conflictColumn='id']
 * @returns {Promise<any[]>}
 */
export async function upsertSupabaseTable(table, rows, conflictColumn = 'id') {
  if (!rows || !rows.length) return [];

  const client = getSupabaseClient();
  if (isMockClient(client) && typeof client.from === 'function') {
    const chunkSize = table === TABLES.APP_SETTINGS ? 100 : 50;
    const results = [];
    for (let index = 0; index < rows.length; index += chunkSize) {
      const chunk = rows.slice(index, index + chunkSize);
      const { data, error } = await client.from(table).upsert(chunk, { onConflict: conflictColumn });
      if (error) {
        console.warn(`Supabase upsert ${table} note:`, error);
        throw new Error(error.message || 'Upsert error');
      }
      if (Array.isArray(data)) results.push(...data);
    }
    return results;
  }

  const chunkSize = table === TABLES.APP_SETTINGS ? 100 : 50;
  const results = [];

  for (let index = 0; index < rows.length; index += chunkSize) {
    const chunk = rows.slice(index, index + chunkSize);
    const responseRows = await supabaseRequest(`${table}?on_conflict=${conflictColumn}`, {
      method: 'POST',
      headers: { Prefer: 'resolution=merge-duplicates,return=minimal' },
      body: JSON.stringify(chunk)
    });
    if (Array.isArray(responseRows)) {
      results.push(...responseRows);
    }
  }

  return results;
}

// ============================================================================
// Database Row Mappers & Converters
// ============================================================================

/**
 * Normalizes a date value for PostgreSQL `date` column.
 * @param {any} val
 * @returns {string|null} YYYY-MM-DD or null
 */
export function normalizeDateForDb(val) {
  if (!val) return null;
  const str = String(val).trim();
  if (!str || str === 'null' || str === 'undefined') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const dt = new Date(str);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString().split('T')[0];
}

/**
 * Recursively converts object keys from snake_case to camelCase.
 * @param {any} obj
 * @returns {any}
 */
export function convertKeysToCamel(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamel(item));
  } else if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key of Object.keys(obj)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      newObj[camelKey] = convertKeysToCamel(obj[key]);
    }
    return newObj;
  }
  return obj;
}

/**
 * Recursively converts object keys from camelCase to snake_case.
 * @param {any} obj
 * @returns {any}
 */
export function convertKeysToSnake(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToSnake(item));
  } else if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key of Object.keys(obj)) {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      newObj[snakeKey] = convertKeysToSnake(obj[key]);
    }
    return newObj;
  }
  return obj;
}

/**
 * Maps an in-memory InventoryItem model to a PostgreSQL `inventory` row.
 * @param {import('../types/index.js').InventoryItem} item
 * @returns {Object}
 */
export function toInventoryRow(item) {
  return {
    id: String(item.id || ''),
    name: item.name || '',
    brand: item.brand || '',
    size: item.size || '',
    quantity: Math.max(0, Number(item.quantity) || 0),
    unit: item.unit || '',
    expiry_date: normalizeDateForDb(item.expiryDate),
    location: item.location || '',
    notes: item.notes || '',
    unit_detail: item.unitDetail || '',
    box_contents: normalizeBoxContents(item.boxContents),
    updated_at: item.updatedAt || new Date().toISOString(),
    image: item.image || '',
    lot: item.lot || '',
    deleted: Boolean(item.deleted),
    created_by: item.createdBy || item.created_by || getAuthenticatedOperatorEmail(),
    updated_by: item.updatedBy || item.updated_by || getAuthenticatedOperatorEmail()
  };
}

/**
 * Maps a PostgreSQL `inventory` row to an in-memory InventoryItem model.
 * @param {Object} row
 * @returns {import('../types/index.js').InventoryItem}
 */
export function fromInventoryRow(row) {
  return {
    id: row.id,
    name: row.name || '',
    brand: row.brand || '',
    size: row.size || '',
    quantity: Number(row.quantity) || 0,
    unit: row.unit || '',
    expiryDate: row.expiry_date || '',
    location: row.location || '',
    notes: row.notes || '',
    unitDetail: row.unit_detail || '',
    boxContents: normalizeBoxContents(row.box_contents),
    updatedAt: row.updated_at || '',
    image: row.image || '',
    lot: row.lot || '',
    deleted: Boolean(row.deleted),
    createdBy: row.created_by || '',
    updatedBy: row.updated_by || ''
  };
}

/**
 * Maps an in-memory Transaction model to a PostgreSQL `transactions` row.
 * @param {import('../types/index.js').Transaction} t
 * @returns {Object}
 */
export function toTransactionRow(t) {
  return {
    id: t.id,
    item_id: t.itemId || '',
    item_name: t.itemName || '',
    borrower_name: t.borrowerName || '',
    quantity: Math.max(0, Number(t.quantity) || 0),
    type: t.type || '',
    timestamp: t.timestamp || new Date().toISOString(),
    notes: t.notes || '',
    unit: t.unit || '',
    box_contents: normalizeBoxContents(t.boxContents),
    updated_at: t.updatedAt || t.timestamp || new Date().toISOString(),
    deleted: Boolean(t.deleted),
    operator_email: t.operatorEmail || t.operator_email || getAuthenticatedOperatorEmail(),
    created_by: t.createdBy || t.created_by || getAuthenticatedOperatorEmail()
  };
}

/**
 * Maps a PostgreSQL `transactions` row to an in-memory Transaction model.
 * @param {Object} row
 * @returns {import('../types/index.js').Transaction}
 */
export function fromTransactionRow(row) {
  let consumedQty = row.consumed_qty !== undefined ? Number(row.consumed_qty) : (row.consumedQty !== undefined ? Number(row.consumedQty) : undefined);
  if (consumedQty === undefined && row.notes) {
    const match = String(row.notes).match(/ใช้ไป\s+([\d.]+)/);
    if (match) {
      consumedQty = parseFloat(match[1]);
    }
  }

  return {
    id: row.id,
    itemId: row.item_id || row.itemId || '',
    itemName: row.item_name || row.itemName || '',
    borrowerName: row.borrower_name || row.borrowerName || '',
    quantity: Number(row.quantity) || 0,
    consumedQty: consumedQty,
    type: row.type || '',
    timestamp: row.timestamp || '',
    notes: row.notes || '',
    unit: row.unit || '',
    boxContents: normalizeBoxContents(row.box_contents || row.boxContents),
    updatedAt: row.updated_at || row.updatedAt || '',
    deleted: Boolean(row.deleted),
    operatorEmail: row.operator_email || row.operatorEmail || '',
    createdBy: row.created_by || row.createdBy || ''
  };
}

/**
 * Maps an in-memory StockAudit model to a PostgreSQL `stock_audits` row.
 * @param {import('../types/index.js').StockAudit} a
 * @returns {Object|null}
 */
export function toStockAuditRow(a) {
  if (!a) return null;
  return {
    id: a.id,
    inspector: a.inspector || '',
    inspector_email: a.inspectorEmail || a.inspector_email || getAuthenticatedOperatorEmail(),
    created_by: a.createdBy || a.created_by || getAuthenticatedOperatorEmail(),
    location: a.location || '',
    status: a.status || 'draft',
    created_at: a.createdAt || new Date().toISOString(),
    updated_at: a.updatedAt || new Date().toISOString(),
    completed_at: a.completedAt || null,
    adjusted: Boolean(a.adjusted),
    audit_items: convertKeysToSnake(a.auditItems || []),
    deleted: Boolean(a.deleted)
  };
}

/**
 * Maps a PostgreSQL `stock_audits` row to an in-memory StockAudit model.
 * @param {Object} row
 * @returns {import('../types/index.js').StockAudit|null}
 */
export function fromStockAuditRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    inspector: row.inspector || '',
    inspectorEmail: row.inspector_email || '',
    createdBy: row.created_by || '',
    location: row.location || '',
    status: row.status || 'draft',
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
    completedAt: row.completed_at || null,
    adjusted: Boolean(row.adjusted),
    auditItems: convertKeysToCamel(row.audit_items || []),
    deleted: Boolean(row.deleted)
  };
}

/**
 * Maps an in-memory StockOutMaster model to a PostgreSQL `stock_out_master` row.
 * @param {import('../types/index.js').StockOutMaster} m
 * @returns {Object|null}
 */
export function toStockOutMasterRow(m) {
  if (!m) return null;
  return {
    id: m.id,
    timestamp: m.timestamp || m.date || m.createdAt || new Date().toISOString(),
    operator_name: m.operatorName || m.operator || m.operator_name || '',
    operator_email: m.operatorEmail || m.operator_email || getAuthenticatedOperatorEmail(),
    created_by: m.createdBy || m.created_by || getAuthenticatedOperatorEmail(),
    reason: m.reason || '',
    deleted: Boolean(m.deleted),
    updated_at: m.updatedAt || m.updated_at || new Date().toISOString()
  };
}

/**
 * Maps a PostgreSQL `stock_out_master` row to an in-memory StockOutMaster model.
 * @param {Object} row
 * @returns {import('../types/index.js').StockOutMaster|null}
 */
export function fromStockOutMasterRow(row) {
  if (!row) return null;
  const ts = row.timestamp || row.created_at || row.updated_at || '';
  const op = row.operator_name || row.operator || row.operator_email || '';
  return {
    id: row.id,
    timestamp: ts,
    date: ts ? ts.split('T')[0] : '',
    createdAt: ts,
    operator: op,
    operatorName: row.operator_name || op,
    operatorEmail: row.operator_email || '',
    createdBy: row.created_by || '',
    reason: row.reason || '',
    deleted: Boolean(row.deleted),
    updatedAt: row.updated_at || ts
  };
}

/**
 * Maps an in-memory StockOutDetail model to a PostgreSQL `stock_out_details` row.
 * @param {import('../types/index.js').StockOutDetail} d
 * @returns {Object|null}
 */
export function toStockOutDetailsRow(d) {
  if (!d) return null;
  return {
    id: d.id,
    stock_out_id: d.stockOutId || d.masterId || d.stock_out_id || '',
    item_id: d.itemId || d.item_id || '',
    item_name: d.itemName || d.item_name || '',
    quantity: Number(d.quantity) || 0,
    unit: d.unit || '',
    deleted: Boolean(d.deleted),
    updated_at: d.updatedAt || d.updated_at || new Date().toISOString()
  };
}

/**
 * Maps a PostgreSQL `stock_out_details` row to an in-memory StockOutDetail model.
 * @param {Object} row
 * @returns {import('../types/index.js').StockOutDetail|null}
 */
export function fromStockOutDetailsRow(row) {
  if (!row) return null;
  const sId = row.stock_out_id || row.stockOutId || row.masterId || '';
  return {
    id: row.id,
    stockOutId: sId,
    masterId: sId,
    itemId: row.item_id || row.itemId || '',
    itemName: row.item_name || row.itemName || '',
    quantity: Number(row.quantity) || 0,
    unit: row.unit || '',
    lot: row.lot || '',
    location: row.location || '',
    expiryDate: row.expiry_date || row.expiryDate || '',
    brand: row.brand || '',
    size: row.size || '',
    deleted: Boolean(row.deleted),
    updatedAt: row.updated_at || row.updatedAt || ''
  };
}
